<div *ngIf="loading && !snapshot" class="loading-only">
  <mat-progress-spinner diameter="44" mode="indeterminate"></mat-progress-spinner>
</div>

<div class="live-page" *ngIf="snapshot as snap">
  <!-- First-run: users who open Live before Agent Companion -->
  <div class="setup-guide-banner" *ngIf="showAgentCompanionSetupBanner">
    <div class="sgb-content">
      <span class="sgb-title">Set up your agent first</span>
      <p class="sgb-text">
        Use <strong>Agent Companion</strong> to choose your runtime (OpenClaw, Claude, …), connect the Local Companion, then come back here for real-time monitoring.
      </p>
    </div>
    <div class="sgb-actions">
      <a class="btn-sgb-primary" routerLink="/desktop/agent-companion">Get started</a>
      <button type="button" class="btn-sgb-secondary" (click)="dismissAgentCompanionSetupBanner()">Dismiss</button>
    </div>
  </div>

  <!-- Chips + savings row -->
  <app-live-top-bar [topline]="snap.topline" [health]="health" [session]="activeSession()"></app-live-top-bar>

  <!-- Status bar -->
  <div class="status-bar">
    <span class="sb-item">
      <span class="sb-label">Companion:</span>
      <span class="sb-value" [class.sb-ok]="health?.['status'] === 'ok'">{{ companionOrigin || '—' }}</span>
    </span>
    <span class="sb-sep"></span>
    <span class="sb-item">
      <span class="sb-label">Stream:</span>
      <span class="sb-value" [class.sb-ok]="sseConnected">{{ sseConnected ? 'live' : 'reconnecting' }}</span>
    </span>
    <span class="sb-sep"></span>
    <span class="sb-item">
      <span class="sb-label">Last event:</span>
      <span class="sb-value">{{ lastEventLabel }}</span>
    </span>
    <span class="sb-sep" *ngIf="health?.['workflowPolicyMode']"></span>
    <span class="sb-item" *ngIf="health?.['workflowPolicyMode']">
      <span class="sb-label">Policy:</span>
      <span class="sb-value">{{ health?.['workflowPolicyMode'] }}</span>
    </span>
  </div>

  <!-- Upgrade hint -->
  <div class="upgrade-hint" *ngIf="snap.topline.metricsPresentation === 'projected' && snap.topline.runMode !== 'off'">
    <mat-icon fontIcon="rocket_launch"></mat-icon>
    <div>
      <strong>Trial ended or license inactive.</strong> Spectyra keeps monitoring — upgrade to realize the
      projected savings shown here.
      <span class="muted-inline">Use the Spectyra web app to upgrade.</span>
    </div>
  </div>

  <!-- Main split layout -->
  <div class="split">
    <div class="split-left">
      <app-agent-activity-panel
        [session]="activeSession()"
        [stepRows]="stepRows"
        [recentEvents]="recentEvents"
      ></app-agent-activity-panel>
    </div>
    <div class="split-right">
      <app-spectyra-intelligence-panel
        [topline]="snap.topline"
        [session]="activeSession()"
        [workflowPolicy]="workflowPolicySummary"
        [stepRows]="stepRows"
      ></app-spectyra-intelligence-panel>

      <!-- Account sync card -->
      <div class="sync-card">
        <div class="sync-header">
          <span class="sync-title">Account sync</span>
        </div>
        <div class="sync-body">
          <div class="sync-row">
            <mat-slide-toggle [checked]="syncToCloud" (change)="onSyncToggle($event.checked)" [disabled]="syncing">
              Sync redacted summaries
            </mat-slide-toggle>
            <button class="btn-sync" (click)="trySyncLatestSession()" [disabled]="!syncToCloud || syncing">
              <mat-icon fontIcon="cloud_upload" style="font-size:14px;width:14px;height:14px"></mat-icon>
              Sync now
            </button>
          </div>
          <p class="sync-err" *ngIf="cloudSync.lastError">{{ cloudSync.lastError }}</p>
          <p class="sync-auth" *ngIf="!canSyncAccount">
            <a routerLink="/login">Sign in</a> to enable cloud sync.
          </p>
        </div>
      </div>
    </div>
  </div>

  <!-- Bottom tab group -->
  <div class="bottom-tabs">
    <mat-tab-group animationDuration="0ms">
      <mat-tab label="Step breakdown">
        <div class="tab-content">
          <table class="step-table" *ngIf="stepRows.length">
            <thead>
              <tr>
                <th>#</th>
                <th>Model</th>
                <th>Tokens in</th>
                <th>Step $ / %</th>
                <th>Transforms</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let row of stepRows">
                <td class="mono">{{ row.stepIndex != null ? row.stepIndex + 1 : '—' }}</td>
                <td>{{ row.model || '—' }}</td>
                <td class="mono">{{ row.inputTokensBefore | number }} → {{ row.inputTokensAfter | number }}</td>
                <td class="mono">${{ row.estimatedStepSavings | number : '1.2-4' }} ({{ row.estimatedStepSavingsPct | number : '1.0-1' }}%)</td>
                <td>{{ (row.transformsApplied || []).join(', ') || '—' }}</td>
              </tr>
            </tbody>
          </table>
          <p class="empty-hint" *ngIf="!stepRows.length">Steps appear as provider calls complete.</p>
        </div>
      </mat-tab>

      <mat-tab label="Prompt compare">
        <div class="tab-content">
          <p *ngIf="liveState?.promptComparisonAvailable && activeSession() as cur">
            <button class="btn-secondary" (click)="openPromptComparison(cur.runId)">Open prompt comparison</button>
          </p>
          <p class="empty-hint" *ngIf="!liveState?.promptComparisonAvailable">No prompt comparison for the current aggregate yet.</p>
        </div>
      </mat-tab>

      <mat-tab label="Transforms">
        <div class="tab-content">
          <div *ngIf="activeSession() as s" class="chips-row">
            <span class="transform-chip" *ngFor="let t of s.transformsApplied">{{ t }}</span>
            <span class="empty-hint" *ngIf="!s.transformsApplied?.length">—</span>
          </div>
          <p class="empty-hint" *ngIf="!activeSession()">No session.</p>
        </div>
      </mat-tab>

      <mat-tab label="Security">
        <div class="tab-content">
          <ul class="trust-list">
            <li>Runs locally — inference goes direct to your provider.</li>
            <li>Telemetry: {{ activeSession()?.security?.telemetryMode || '—' }}</li>
            <li>Prompt snapshots: {{ activeSession()?.security?.promptSnapshotMode || '—' }}</li>
            <li>Optimization: {{ snap.topline.optimizationHeadline }}</li>
          </ul>
        </div>
      </mat-tab>

      <mat-tab label="Raw events">
        <div class="tab-content">
          <ul class="event-list" *ngIf="recentEvents.length">
            <li *ngFor="let e of recentEvents.slice().reverse()">
              <span class="mono">{{ e.type }}</span>
              <span class="ev-sep">·</span>
              <span class="mono">{{ e.sessionId | slice:0:8 }}…</span>
              <span class="ev-sep">·</span>
              <span class="mono">{{ e.timestamp | date : 'shortTime' }}</span>
            </li>
          </ul>
          <p class="empty-hint" *ngIf="!recentEvents.length">No persisted events (or telemetry off).</p>
        </div>
      </mat-tab>
    </mat-tab-group>
  </div>
</div>
